import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { ApiError } from '@/shared/api/client'
import { useUpdateTeamName } from '../hooks/useUserLeagues'
import styles from './EditTeamNameSheet.module.css'

type EditTeamNameSheetProps = {
  leagueId:    string
  currentName: string  // currently displayed name (alias or username fallback)
  onClose:     () => void
}

const MAX_LENGTH = 30

export function EditTeamNameSheet({ leagueId, currentName, onClose }: EditTeamNameSheetProps) {
  const [name, setName]   = useState(currentName)
  const [error, setError] = useState('')
  const sheetRef = useRef<HTMLFormElement>(null)

  const mutation = useUpdateTeamName(leagueId)

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  useEffect(() => {
    sheetRef.current?.focus()
  }, [])

  const trimmed = name.trim()
  const isValid = trimmed.length === 0 || trimmed.length >= 3

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) {
      setError('O nome do time deve ter pelo menos 3 caracteres.')
      return
    }
    setError('')
    mutation.mutate(trimmed || null, {
      onSuccess: () => onClose(),
      onError: (err: unknown) => {
        if (err instanceof ApiError) {
          switch (err.errorCode) {
            case 'team_name_taken':
              setError('Este nome de time já está em uso nesta liga.')
              return
            case 'team_name_locked':
              setError('O nome do time não pode mais ser alterado — a competição já começou.')
              return
          }
        }
        setError('Não foi possível salvar. Tente novamente.')
      },
    })
  }

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Editar nome do time"
    >
      <form
        ref={sheetRef}
        className={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        tabIndex={-1}
        noValidate
      >
        <div className={styles.header}>
          <span className={styles.title}>Nome do seu time</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.hint}>
            Este é o nome do seu time apenas nesta liga. Deixe em branco para usar seu nome de usuário.
          </p>

          <input
            className={styles.input}
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); if (error) setError('') }}
            placeholder="Ex: Fantyx FC"
            maxLength={MAX_LENGTH}
            autoComplete="off"
            spellCheck={false}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label="Nome do seu time nesta liga"
            aria-invalid={!!error}
          />

          {error && <span className={styles.error} role="alert">{error}</span>}

          <button type="submit" className={styles.save} disabled={!isValid || mutation.isPending}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  )
}
